import type { OrderObject, SavingRule } from "../../types";
import type {
	PaginatedObjectsResult,
	QuotationMetrics,
	QuotationResultTotals,
	QuotationSummaryTextInput,
} from "./QuotationSummary.types";
import type { CalculateQuotationResult } from "../../services/quotationService";
import type { PaymentMethodOption } from "../../services/paymentClosingService";
import type { PaymentFlowKind } from "../../services/paymentFlowKind";

export interface ClosingChecklistItem {
	id: string;
	label: string;
	done: boolean;
}

export interface ClosingChecklistInput {
	flowKind: PaymentFlowKind;
	hasPaymentMethod: boolean;
	hasGeneratedPix: boolean;
	hasCopiedPix: boolean;
	hasComprovante: boolean;
}

export function buildClosingChecklist(
	input: ClosingChecklistInput,
): ClosingChecklistItem[] {
	const items: ClosingChecklistItem[] = [];

	if (input.flowKind === "sys_pix_qr") {
		items.push(
			{
				id: "pix_generated",
				label: "PIX gerado (QR Code)",
				done: input.hasGeneratedPix,
			},
			{
				id: "pix_copied",
				label: "Codigo PIX copiado para o cliente",
				done: input.hasCopiedPix,
			},
		);
	}

	if (input.flowKind === "pix_comprovante") {
		items.push({
			id: "comprovante",
			label: "Comprovante da transferencia PIX anexado",
			done: input.hasComprovante,
		});
	}

	return items;
}

export function getClosingStatusLabel(input: {
	hasSavedFile: boolean;
	flowKind: PaymentFlowKind;
}): string {
	if (!input.hasSavedFile) {
		return "Fechamento em andamento";
	}

	switch (input.flowKind) {
		case "sys_pix_qr":
			return "Aguardando PIX";
		case "pix_comprovante":
			return "Aguardando financeiro";
		case "caixa":
			return "Aguardando caixa";
		default:
			return "Arquivo salvo";
	}
}

export function getPostSaveSuccessNotification(
	orcId: number,
	flowKind: PaymentFlowKind,
): { title: string; message: string } {
	switch (flowKind) {
		case "sys_pix_qr":
			return {
				title: "Arquivo salvo",
				message: `Arquivo salvo. Pedido #${orcId} aguardando pagamento PIX (confirmacao automatica).`,
			};
		case "pix_comprovante":
			return {
				title: "Arquivo salvo",
				message: `Arquivo salvo. Comprovante enviado. Pedido #${orcId} aguarda validacao do financeiro.`,
			};
		case "caixa":
			return {
				title: "Arquivo salvo",
				message: `Arquivo salvo. Encaminhe o cliente ao caixa para pagar o pedido #${orcId}.`,
			};
		default:
			return {
				title: "Arquivo salvo",
				message: `Pedido #${orcId} registrado com arquivo salvo no Corel.`,
			};
	}
}

export function getSaveFileButtonHint(input: {
	flowKind: PaymentFlowKind;
	hasPaymentMethod: boolean;
	hasGeneratedPix: boolean;
	hasCopiedPix: boolean;
	hasComprovante: boolean;
	hasSavedFile: boolean;
}): string {
	if (input.hasSavedFile) {
		return "Arquivo ja salvo para este pedido.";
	}

	if (!input.hasPaymentMethod) {
		return "Selecione a forma de pagamento para habilitar Fechar pedido e salvar no Corel.";
	}

	if (input.flowKind === "sys_pix_qr") {
		if (!input.hasGeneratedPix) {
			return "Gere o PIX antes de salvar o arquivo.";
		}
		if (!input.hasCopiedPix) {
			return "Copie o codigo PIX antes de salvar o arquivo.";
		}
	}

	if (input.flowKind === "pix_comprovante" && !input.hasComprovante) {
		return "Anexe o comprovante da transferencia PIX antes de salvar o arquivo.";
	}

	return "Salva o arquivo no Corel e registra o fechamento comercial do pedido.";
}

export function getComprovanteSectionTooltip(
	flowKind: PaymentFlowKind,
): string {
	if (flowKind === "pix_comprovante") {
		return "Obrigatorio: comprovante da transferencia PIX para a conta da empresa.";
	}
	return "Opcional: anexe se o cliente ja tiver comprovante. Caso contrario, o caixa valida no pagamento.";
}

export const AUTO_PIX_STORAGE_KEY = "quotation:auto_pix_enabled";

export function getInitialAutoPixEnabled(): boolean {
	if (typeof window === "undefined") return false;

	try {
		return window.localStorage.getItem(AUTO_PIX_STORAGE_KEY) === "1";
	} catch {
		return false;
	}
}

export function calculateQuotationMetrics(
	objects: OrderObject[],
	savingRule: SavingRule | null,
): QuotationMetrics {
	if (!savingRule) {
		const totalMetragem = roundUpTwoDecimals(
			objects.reduce((sum, obj) => sum + (obj.height * obj.quantity) / 100, 0),
		);

		return {
			totalMetragem,
			totalUnidades: objects.reduce((sum, obj) => sum + obj.quantity, 0),
			isMetros: true,
		};
	}

	const isMetros = savingRule.umd_id === 3;
	if (isMetros) {
		return {
			totalMetragem: roundUpTwoDecimals(
				objects.reduce((sum, obj) => sum + (obj.height * obj.quantity) / 100, 0),
			),
			totalUnidades: 0,
			isMetros: true,
		};
	}

	return {
		totalMetragem: 0,
		totalUnidades: objects.reduce((sum, obj) => sum + obj.quantity, 0),
		isMetros: false,
	};
}

export function getPaginatedObjects(
	objects: OrderObject[],
	currentPage: number,
	itemsPerPage: number,
): PaginatedObjectsResult {
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedObjects = objects.slice(startIndex, endIndex);
	const totalPages = Math.ceil(objects.length / itemsPerPage);
	const indexOfLastItem = Math.min(startIndex + itemsPerPage, objects.length);

	return {
		paginatedObjects,
		totalPages,
		indexOfFirstItem: startIndex,
		indexOfLastItem,
	};
}

export function getQuotationResultTotals(
	quotationResult: CalculateQuotationResult | null,
): QuotationResultTotals {
	if (!quotationResult) {
		return {
			precoFinal: 0,
			valorTotalFinal: 0,
			descontoAplicado: 0,
			fonteCalculo: null,
		};
	}

	let descontoAplicado = 0;
	if (quotationResult.fonte === "discount" && quotationResult.desconto) {
		descontoAplicado =
			quotationResult.desconto.product_base_price -
			quotationResult.desconto.final_price;
	}

	return {
		precoFinal: quotationResult.preco_unitario,
		valorTotalFinal: quotationResult.valor_total,
		descontoAplicado,
		fonteCalculo: quotationResult.fonte,
	};
}

export function calculateCardPaymentText(valorTotal: number): string {
	const valorComJuros6 = valorTotal * 1.06;
	return `💳 Cartao credito com as taxas 6x: Total R$ ${valorComJuros6.toFixed(2)}`;
}

export const DEFAULT_QUOTATION_SUMMARY_BODY = `📌 ORCAMENTO {{produtoCompleto}} [{{dataAtual}}]

💵 A vista: PIX/Dinheiro/Debito: *R$ {{valorVista}}*

🙋ATENDENTE: {{atendenteNome}}🙅
━━━━━━━━━━━━━━━━━━━━━
👤 Cliente: {{clienteNome}}
━━━━━━━━━━━━━━━━━━━━━
📏 Metragem Total: {{metragemDisplay}} metros
━━━━━━━━━━━━━━━━━━━━━
💰 Outras formas de Pagamento:
💳 Cartão de credito com acrescimo
━━━━━━━━━━━━━━━━━━━━━
⚠️ IMPORTANTE⚠️
Confira atentamente todas as informacoes🔎
Apos a aprovacao e confirmacao do pagamento, NAO realizamos cancelamentos ou alteracoes no pedido.❌

STATUS: ORCAMENTO AGUARDANDO APROVACAO e PAGAMENTO`;

/**
 * Substitui placeholders {{campo}} pelos valores fornecidos.
 * Placeholders sem valor correspondente sao mantidos como estao.
 */
export function renderQuotationTemplate(
	template: string,
	values: Record<string, string>,
): string {
	return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
		const value = values[key];
		return value === undefined || value === null ? match : String(value);
	});
}

export function buildQuotationSummaryText(
	input: QuotationSummaryTextInput,
	templateBody?: string,
): string {
	const produtoCompleto = `${input.produtoNome}${input.qualidadeNome ? ` ${input.qualidadeNome}` : ""
		}`.toUpperCase();

	const body = templateBody?.trim()
		? templateBody
		: DEFAULT_QUOTATION_SUMMARY_BODY;

	return renderQuotationTemplate(body, {
		produtoCompleto,
		dataAtual: input.dataAtual,
		valorVista: input.valorVista,
		atendenteNome: input.atendenteNome.toUpperCase(),
		clienteNome: input.clienteNome,
		metragemDisplay: input.metragemDisplay,
		paymentOptions: input.paymentOptions,
	}).trim();
}

export function getPaymentMethodHint(
	selectedPaymentMethod: PaymentMethodOption | null,
): string | null {
	if (!selectedPaymentMethod) return null;

	if (selectedPaymentMethod.id === 5) {
		return "Gere o QR Code SYS.PIX e envie ao cliente. Depois use Salvar arquivo — o pedido aguarda confirmacao automatica do PIX.";
	}

	if (selectedPaymentMethod.id === 13) {
		return "Cliente pagou na conta da empresa? Anexe o comprovante da transferencia — o financeiro valida.";
	}

	if (
		selectedPaymentMethod.permiteParcelamento &&
		selectedPaymentMethod.maxParcelas
	) {
		return `Permite parcelamento em ate ${selectedPaymentMethod.maxParcelas}x.`;
	}

	return "Sem parcelamento detalhado nesta etapa.";
}

export function getClosingActionHint(
	selectedPaymentMethod: PaymentMethodOption | null,
	flowKind: PaymentFlowKind,
	hasSelectedClientDocument: boolean,
): string {
	if (!selectedPaymentMethod) {
		return "Selecione a forma de pagamento. O vendedor nao libera producao — o pedido fica em espera apos salvar o arquivo.";
	}

	if (flowKind === "sys_pix_qr") {
		if (!hasSelectedClientDocument) {
			return "SYS.PIX selecionado, mas o cliente esta sem CPF/CNPJ. Atualize o cadastro para gerar o QR Code.";
		}
		return "Gere o PIX, copie o codigo para o cliente e entao use Salvar arquivo.";
	}

	if (flowKind === "pix_comprovante") {
		return "Anexe o comprovante da transferencia PIX. Apos Salvar arquivo, o financeiro valida o pagamento.";
	}

	return "Informe a forma de pagamento. O cliente vai ao caixa — la o pedido e liberado. Comprovante e opcional aqui.";
}

export function getSaveNowHint(oitId: number | null | undefined): string | null {
	if (!oitId) {
		return "Item ainda nao disponivel para salvamento rapido no Gerenciador.";
	}
	return "Salva no Gerenciador com nome [O-]/[I-], sem exigir forma de pagamento.";
}

export function normalizeEditedMeasurement(
	rawValue: string,
): { displayValue: string; numericValue: number | null } {
	const cleaned = rawValue.replace(/[^\d,.-]/g, "");
	const parts = cleaned.split(/[,.]/);
	let formatted = parts[0] || "";

	if (parts.length > 1) {
		formatted += `,${parts.slice(1).join("").substring(0, 2)}`;
	}

	const numericValue = Number.parseFloat(formatted.replace(",", "."));
	return {
		displayValue: formatted,
		numericValue: Number.isNaN(numericValue) ? null : numericValue,
	};
}

export function formatMeasurementInput(
	value: number,
	isMetros: boolean,
): string {
	return isMetros ? value.toFixed(2).replace(".", ",") : value.toString();
}

function roundUpTwoDecimals(value: number): number {
	return Math.ceil(value * 100) / 100;
}
